import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../store/store";
import PreviewNav from "../components/PreviewNav";
import HeaderVerified from "../components/header/HeaderVerified";
import SidebarVerified from "../components/sidebar/SidebarVerified";
import HelpAndSupport from "../components/HelpAndSupport/HelpAndSupport";
import EmailVerificationBanner from "../components/EmailVerificationBanner";

/** Same shell as UserLayout with a preview strip on top, for design mockups */
export const PreviewLayout: React.FC = () => {
  const [showHelp, setShowHelp] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const userPhotoUrl = useSelector((state: RootState) => state.auth.photoUrl);

  const closeHelp = useCallback(() => setShowHelp(false), []);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-canvas">
      <PreviewNav />

      <div className="flex min-h-0 flex-1">
        <SidebarVerified mobileOpen={navOpen} onMobileOpenChange={setNavOpen} />

        <div className="flex min-w-0 flex-1 flex-col">
          <HeaderVerified
            handleClick={() => setShowHelp(true)}
            onMenuClick={() => setNavOpen(true)}
            userAvatar={userPhotoUrl || undefined}
          />
          <EmailVerificationBanner />
          <main className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>

      {showHelp &&
        createPortal(
          <div className="fixed inset-0 z-[1050] flex justify-end">
            <div className="absolute inset-0 animate-fade-in bg-gray-950/40 backdrop-blur-[2px]" onClick={closeHelp} />
            <aside
              role="dialog"
              aria-modal="true"
              aria-label="Help and support"
              className="relative h-full w-full max-w-xl animate-rise overflow-y-auto bg-white px-6 shadow-overlay"
            >
              <HelpAndSupport onButtonClick={closeHelp} />
            </aside>
          </div>,
          document.body
        )}
    </div>
  );
};
